import React, { FC, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Switch } from 'react-router';

import { ContentType, Page } from 'api/contentful/contentTypes.cloverdotcom';
import { useContentEntriesContext } from 'components/ContentEntriesProvider/ContentEntriesContext';
import { AutoContentfulEntry } from 'components/EntryLoader/AutoContentfulEntry';
import { NotFoundPage } from 'components/ErrorPage/NotFoundPage';
import { PageAssembly } from 'components/PageAssembly/PageAssembly';
import { useAllContentfulEntries } from 'hooks/useAllContentfulEntries';
import { useLowercasePathname } from 'hooks/useLowercasePathname';
import { useBaseUrlFromLocale } from 'utils/AppRoute';
import { getLocaleRouteBySlug } from 'utils/getLocaleRouteBySlug';
import { sortPagesBySlug } from './sortPagesBySlug';
import { getRequestDepth } from 'utils/getRequestDepth';

type PagesSwitchProps = {
  children?: ReactNode;
};

export const PagesSwitch: FC<PagesSwitchProps> = ({ children }) => {
  useLowercasePathname();
  const { language } = useTranslation().i18n;
  const baseUrl = useBaseUrlFromLocale(language);
  const { items, setItems } = useContentEntriesContext();
  const { data, isLoading } = useAllContentfulEntries<Page>(ContentType.BASIC_PAGE, {
    select: { sys: 'id', fields: ['slug', 'verticalMarket', 'includeSheetModal'] },
  });

  useEffect(() => {
    if (!data) return;
    const pages = sortPagesBySlug(data);
    setItems(pages);
  }, [isLoading]);

  if (isLoading) return null;

  return (
    <Switch>
      {items.map(item => {
        const routePath = getLocaleRouteBySlug(item.slug, baseUrl);
        return (
          <Route
            key={item.id}
            exact
            path={routePath}
            render={() => (
              <AutoContentfulEntry
                entryId={item.id}
                include={getRequestDepth(item)}
                render={entry => <PageAssembly entry={entry} />}
              />
            )}
          />
        );
      })}
      {children}
      <Route render={() => <NotFoundPage />} />
    </Switch>
  );
};
